import React from "react";
import { Col, Form, Row } from 'react-bootstrap';
import TypedSelect from "common/ui/TypedSelect";
import * as _ from "lodash";
import { CustomField, CustomFieldOption, CustomFieldThin, CustomFieldType } from "domain/customField";
import classNames from "classnames";
import { formatDate } from "common/formatters";
import moment from "moment";
import NumberEditor from "common/ui/NumberEditor";
import Multiselect from "react-multi-select-component";
import { localizeOfferContract as localize, localizeCommon } from "features/localization/localizeService";
import "./AddCustomFieldModal.scss";

export interface AddCustomFieldModalProps {
    customFields: CustomField[];
    selectedCustomFields: CustomFieldThin[];
    onCustomFieldsChanged(customFields: CustomFieldThin[]): void;
}

export interface CustomFieldProps {
    field: CustomField;
    selectedValue: string;
    onValueChanged(value: string): void;
}

const CustomFieldComponent: React.FC<CustomFieldProps> = props => {

    switch (props.field.type) {
        case CustomFieldType.SingleSelect:
            return <SingleSelect {...props} />;
        case CustomFieldType.Date:
            return <Date {...props} />;
        case CustomFieldType.Decimal:
            return <Decimal {...props} />;
        case CustomFieldType.String:
            return <FreeText {...props} />;
        case CustomFieldType.MultiSelect:
            return <MultiSelectComponent {...props} />;
        default:
            return <></>;
    }
}

const SingleSelect: React.FC<CustomFieldProps> = props => {
    return <TypedSelect
        className="cf-select"
        nullRowText={localizeCommon("Value_Select")}
        showNullRow
        required={props.field.isRequired}
        value={props.field.allowedOptions.find(opt => opt.erpId === props.selectedValue)}
        options={props.field.allowedOptions}
        valueGetter={cf => cf.erpId}
        textGetter={cf => cf.description}
        onValueSelected={cf => props.onValueChanged(cf?.erpId)} />
}

const MultiSelectComponent: React.FC<CustomFieldProps> = props => {
    const selectedValues = props.selectedValue?.split(",");
    const options = props.field.allowedOptions.map(x => getOption(x));
    return <Multiselect
        className="cf-multi-select"
        labelledBy={localizeCommon("Value_Select")}
        disableSearch
        hasSelectAll
        value={options.filter(m => selectedValues?.includes(m.value))}
        options={options}
        valueRenderer={items => getFilterText(items)}
        onChange={(items: Option[]) => props.onValueChanged(items.map(x => x.value).join(","))} />
}


function getFilterText(selected: Option[]): string {
    if (selected.length === 0) return localizeCommon("Value_Select");

    if (selected.length === 1) return selected[0].label;

    return localizeCommon("MultiSelect_Value_Selected", {count: selected.length});
}

interface Option {
    value: string;
    label: string;
}

function getOption(item: CustomFieldOption): Option {
    return { value: item.erpId, label: item.description };
}

const Date: React.FC<CustomFieldProps> = props => {
    const supportedFormat = "yyyy-MM-DD"; //TODO: Add Support in DB for future
    return <Form.Control
        type="date"
        required={props.field.isRequired}
        value={formatDate(props.selectedValue, supportedFormat)}
        min={props.field.minValue ? formatDate(props.field.minValue, supportedFormat) : null}
        max={props.field.maxValue ? formatDate(props.field.maxValue, supportedFormat) : null}
        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onValueChanged(moment(ev.target.value).format(supportedFormat))}>
    </Form.Control>
}

const Decimal: React.FC<CustomFieldProps> = props => {
    return <NumberEditor
        className="form-control"
        required={props.field.isRequired}
        allowDecimals
        precision={4}
        min={Number(props.field.minValue)}
        max={Number(props.field.maxValue)}
        placeholder={"0.0000"}
        value={props.selectedValue}
        onValueChange={value => props.onValueChanged(value?.toString())}
    />
}

const FreeText: React.FC<CustomFieldProps> = props => {
    return <Form.Control
        value={props.selectedValue}
        required={props.field.isRequired}
        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onValueChanged(ev.target.value)}>
    </Form.Control>
}

const AddCustomFieldModal: React.FC<AddCustomFieldModalProps> = props => {
    const groups = _.orderBy(
        _.uniqBy(props.customFields.map(cf => cf.group).filter(cfg => cfg != null), cfg => cfg.name),
        cfg => cfg.sortOrder);

    const ungroupedFields = props.customFields?.filter(cf => !cf.group);

    function onValueChanged(customField: CustomField, value: string): void {
        let updatedCustomFields = [...props.selectedCustomFields];
        if (props.selectedCustomFields.some(scf => scf.id === customField.id)) {
            updatedCustomFields = updatedCustomFields.map(scf => scf.id === customField.id ? { ...scf, value: value } : scf);
        } else {
            updatedCustomFields.push({id: customField.id, name: customField.name, value: value } as CustomFieldThin);
        }
        props.onCustomFieldsChanged(updatedCustomFields);
    }

    function renderCustomField(cf: CustomField, index: number) {
        return <Form.Group key={index} as={Row}>
            <Form.Label className={classNames({ "optional": !cf.isRequired }, "col-md-4")}>{cf.name}</Form.Label>
            <Col md="8">
                <CustomFieldComponent
                    field={cf}
                    selectedValue={props.selectedCustomFields.find(scf => scf.id === cf.id)?.value}
                    onValueChanged={v => onValueChanged(cf, v)} />
            </Col>
        </Form.Group>
    }

    return <div className="add-custom-field-modal">
        {groups.map((g, idx) =>
            <>
                <div key={`group-${idx}`} className="h5 mt-2">{g.name}</div>
                {
                    _.orderBy((props.customFields ?? [])
                        .filter(cf => cf.group?.name === g.name), cf => cf.sortOrder)
                        .map((cf, index) => renderCustomField(cf, index))
                }
            </>
        )}
        {groups.length > 0 && ungroupedFields.length > 0 && <div className="h5 mt-2">{localize("AddCustomFieldModal_Label_AdditionalFields")}</div>}
        {
            _.orderBy(ungroupedFields, cf => cf.sortOrder).map((cf, index) => renderCustomField(cf, index))
        }
    </div>;
}

export default AddCustomFieldModal;